import { useCallback, useEffect, useRef } from 'react'
import type { TableVirtuosoHandle } from 'react-virtuoso'

interface KeepBoardAtTop {
    /** Wire to TableVirtuoso's `ref`. */
    tableRef: React.MutableRefObject<TableVirtuosoHandle | null>
    /** Wire to TableVirtuoso's `atTopStateChange`, against the table's `atTopThreshold`. */
    atTopStateChange: (atTop: boolean) => void
}

/**
 * Keep a board that is AT THE TOP showing its top row when a new one arrives above it.
 *
 * A virtualized list does not move its viewport when rows are prepended: the scroll offset is a number of
 * pixels, and inserting a row above simply pushes everything down by one row height. Unless the offset is
 * exactly zero, the new row lands ABOVE the visible area — present, rendered, and invisible. On a live feed
 * that reads as "the socket stopped working": the operator sees nothing arrive, scrolls, comes back, and
 * finds the load sitting at the top all along. (The board had a scroll-to-new behind an Auto Refresh
 * checkbox until #12009 retired the whole cluster, which is what left this gap.)
 *
 * So when something new appears at the FRONT and the operator is at the top, the list is pinned back to the
 * top. Scrolled away, it is left alone — a reader looking at row forty must never be yanked upwards by
 * freight arriving somewhere they are not looking.
 *
 * @param rows the rows on screen, in display order.
 */
export const useKeepBoardAtTop = (rows: readonly unknown[]): KeepBoardAtTop => {
    const tableRef = useRef<TableVirtuosoHandle | null>(null)
    // Starts true: a board that has never been scrolled IS at its top.
    const atTopRef = useRef(true)
    const previousTopRef = useRef<unknown>(rows[0])

    const atTopStateChange = useCallback((atTop: boolean) => {
        atTopRef.current = atTop
    }, [])

    useEffect(() => {
        const previousTop = previousTopRef.current
        previousTopRef.current = rows[0]
        // Nothing new at the front: a page appended at the end, an overlay refresh, a re-render. Compared by
        // identity, because a prepend puts a different object first while filtering and re-rendering keep the
        // same row objects.
        if (rows[0] === previousTop) return
        // A PREPEND pushes the old top row down; it is still here. A board that was REPLACED — another tab
        // thawed, a new search — does not carry it, and must be left alone: that tab restores the offset the
        // operator left it at, and scrolling it to the top here would throw that away.
        if (previousTop !== undefined && !rows.includes(previousTop)) return
        if (!atTopRef.current) return
        tableRef.current?.scrollToIndex({ index: 0 })
    }, [rows])

    return { tableRef, atTopStateChange }
}
